import React, { useEffect, useState } from "react";
import {
  Loader2,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
  FlaskConical,
  AlertTriangle,
  MinusCircle,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { PageHeader, SectionHeader, Card } from "./adminUi";
import { toast } from "sonner";

/**
 * Master Admin — Trial reminder status.
 *
 * Server-driven. Reminders are dispatched by the FastAPI scheduler every
 * 60 seconds independently of any browser session. This page reads the
 * per-user status Mongo already records and lets the operator:
 *   - Run one immediate scheduler tick ("Run tick now")
 *   - Prepare their OWN account for a live 48h test (one-shot)
 *   - Prepare their OWN account for a live expired-trial test (one-shot)
 *
 * Both Prepare buttons are strictly self-only + one-shot server-side —
 * a rapid double-click cannot produce a duplicate send.
 *
 * Columns render explicit categorical labels (Sent / Not Sent /
 * Not Eligible / Failed) so a blank field is never ambiguous.
 */
export default function AdminTrialRemindersPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [prep48hBusy, setPrep48hBusy] = useState(false);
  const [prepExpBusy, setPrepExpBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // "48h" | "expired" | null
  const [prepareResult, setPrepareResult] = useState(null); // {mode, payload}

  const load = () => {
    adminApi
      .trialReminders({ include_test: includeTest })
      .then((r) => setRows(r?.rows || []))
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Unable to load trial reminders")
      );
  };
  useEffect(() => {
    load();
  }, [includeTest]);

  const runNow = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const r = await adminApi.trialRemindersProcess();
      const c = r?.counts || {};
      toast.success(
        `Tick ran · 48h checked ${c.checked_48h ?? 0} · expired checked ${c.checked_expired ?? 0}`
      );
      setTimeout(load, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run tick.");
    } finally {
      setProcessing(false);
    }
  };

  const prepareSelf = async () => {
    const mode = confirm;
    if (!mode) return;
    const setBusy = mode === "48h" ? setPrep48hBusy : setPrepExpBusy;
    setBusy(true);
    try {
      const call =
        mode === "48h"
          ? adminApi.trialRemindersPrepareSelf
          : adminApi.trialRemindersPrepareExpiredSelf;
      const r = await call();
      setPrepareResult({ mode, payload: r });
      setConfirm(null);
      toast.success(
        mode === "48h"
          ? "Your account is prepared for a 48h reminder test. Nothing sent yet — click Run tick now."
          : "Your account is prepared for an expired-trial reminder test. Nothing sent yet — click Run tick now."
      );
      setTimeout(load, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prepare failed.");
    } finally {
      setBusy(false);
    }
  };

  const list = rows || [];

  return (
    <div data-testid="admin-trial-reminders-page" className="space-y-8">
      <PageHeader
        eyebrow="Retention"
        title="Trial Reminders"
        subtitle="Server-driven 48-hour and expiration reminders (email + SMS) for 3-day Pro trial users. Sends run every 60 seconds and are deduped per-user. Failures are logged with a category — never as 'sent'."
        right={
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="trial-reminders-prepare-self"
              type="button"
              disabled={prep48hBusy}
              onClick={() => {
                setPrepareResult(null);
                setConfirm("48h");
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-50 text-[#B7D2AF] border border-white/10 text-sm font-medium px-3.5 py-2 transition-colors"
              title="One-shot: prepare only your own account for a live 48h reminder test."
            >
              <FlaskConical size={13} /> Prepare 48h test on my account
            </button>
            <button
              data-testid="trial-reminders-prepare-expired-self"
              type="button"
              disabled={prepExpBusy}
              onClick={() => {
                setPrepareResult(null);
                setConfirm("expired");
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-50 text-[#B7D2AF] border border-white/10 text-sm font-medium px-3.5 py-2 transition-colors"
              title="One-shot: prepare only your own account for a live expired-trial reminder test."
            >
              <FlaskConical size={13} /> Prepare expired test on my account
            </button>
            <button
              data-testid="trial-reminders-run-now"
              type="button"
              disabled={processing}
              onClick={runNow}
              className="inline-flex items-center gap-2 rounded-xl bg-[#4F6F52] hover:bg-[#5A7E5D] disabled:bg-[#2A3A2D] text-white text-sm font-semibold px-4 py-2 transition-colors"
            >
              {processing ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <PlayCircle size={14} /> Run tick now
                </>
              )}
            </button>
          </div>
        }
      />

      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs text-[#B7D2AF]">
          <input
            type="checkbox"
            data-testid="trial-reminders-include-test"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            className="rounded border-white/20 bg-black/40"
          />
          Include test/demo users
        </label>
      </div>

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      {prepareResult?.payload?.ok && (
        <div
          data-testid="trial-reminders-prepare-result"
          className="rounded-2xl border border-[#4F6F52]/60 bg-[#0F1B14] text-[#B7D2AF] px-4 py-4"
        >
          <div className="flex items-start gap-2">
            <CheckCircle2 size={16} className="mt-0.5 text-[#8FBF87]" />
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold">
                {prepareResult.mode === "48h"
                  ? "Your account is prepared for a 48h reminder test"
                  : "Your account is prepared for an expired-trial reminder test"}
              </div>
              <div className="text-xs text-[#8FA894] mt-1">
                {prepareResult.payload.email} · nothing sent yet. Click{" "}
                <span className="text-[#B7D2AF] font-medium">Run tick now</span>{" "}
                (above) or wait up to 60s for the automatic tick.
              </div>
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-[#8FA894] hover:text-[#B7D2AF]">
                  What changed on my account?
                </summary>
                <pre className="mt-2 bg-black/40 rounded-lg p-3 overflow-x-auto text-[11px] text-[#B7D2AF]">
                  {JSON.stringify(prepareResult.payload.diff, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      <section>
        <SectionHeader label={`Trial users (${list.length})`} />
        <Card className="overflow-x-auto p-0">
          <table
            className="w-full text-sm"
            data-testid="trial-reminders-table"
          >
            <thead className="text-[10px] uppercase tracking-[0.14em] text-[#8FA894] font-semibold">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Trial Started</th>
                <th className="text-left px-4 py-3">Trial Expires</th>
                <th className="text-center px-3 py-3">48h Email</th>
                <th className="text-center px-3 py-3">48h SMS</th>
                <th className="text-center px-3 py-3">Expiry Email</th>
                <th className="text-center px-3 py-3">Expiry SMS</th>
                <th className="text-center px-3 py-3">SMS Consent</th>
              </tr>
            </thead>
            <tbody className="text-[#E9F1E4]">
              {list.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-[#8FA894]">
                    No trial users found.
                  </td>
                </tr>
              )}
              {list.map((r) => (
                <tr
                  key={r.user_id}
                  data-testid={`trial-reminder-row-${r.user_id}`}
                  className="border-b border-white/5"
                >
                  <td className="px-4 py-3 whitespace-nowrap max-w-[280px] truncate">
                    {r.email}
                    {r.is_test && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-[#8C7A4F]">
                        test
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <PlanBadge status={r.plan_status} />
                  </td>
                  <td className="px-4 py-3 text-[#8FA894] whitespace-nowrap">
                    {formatTs(r.trial_started_at)}
                  </td>
                  <td className="px-4 py-3 text-[#8FA894] whitespace-nowrap">
                    {formatTs(r.trial_expires_at)}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusBadge
                      status={r.reminder_48h_email_status}
                      error={r.reminder_48h_email_error}
                      sentAt={r.reminder_48h_sent_at}
                      testid={`row-48h-email-${r.user_id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusBadge
                      status={r.reminder_48h_sms_status}
                      error={r.reminder_48h_sms_error}
                      sentAt={r.reminder_48h_sent_at}
                      testid={`row-48h-sms-${r.user_id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusBadge
                      status={r.expired_email_status}
                      error={r.expired_email_error}
                      sentAt={r.expired_sent_at}
                      testid={`row-expired-email-${r.user_id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusBadge
                      status={r.expired_sms_status}
                      error={r.expired_sms_error}
                      sentAt={r.expired_sent_at}
                      testid={`row-expired-sms-${r.user_id}`}
                    />
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.sms_opt_in ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[#8FBF87]">
                        <CheckCircle2 size={13} /> opted in
                      </span>
                    ) : r.sms_opted_out ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[#B4795C]">
                        <XCircle size={13} /> opted out
                      </span>
                    ) : (
                      <span className="text-[#6B7A6B] text-xs">no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>

      {/* Prepare confirm modal (shared by 48h + expired) */}
      {confirm && (
        <div
          data-testid={
            confirm === "48h"
              ? "trial-reminders-prepare-modal"
              : "trial-reminders-prepare-expired-modal"
          }
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => {
            if (!prep48hBusy && !prepExpBusy) setConfirm(null);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141B18] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-display text-xl inline-flex items-center gap-2">
              <FlaskConical size={16} className="text-[#B7D2AF]" />
              {confirm === "48h"
                ? "Prepare 48h test on your account"
                : "Prepare expired-trial test on your account"}
            </h3>
            <p className="text-sm text-[#B7D2AF] mt-3 leading-relaxed">
              This will re-seed <strong>only</strong> your currently signed-in
              Master Admin account so the next scheduler tick treats you as a{" "}
              {confirm === "48h"
                ? "mid-trial user with about 1 day remaining"
                : "just-expired trial user"}
              .
            </p>
            <ul className="text-xs text-[#8FA894] mt-3 space-y-1 list-disc pl-5">
              <li>Cannot target another user (no email/id parameter accepted).</li>
              <li>
                One-shot: subsequent clicks are refused server-side so a
                double-click never sends duplicate messages.
              </li>
              <li>
                Preserves your SMS opt-in, subscription status, journal, saved
                wisdom, and every other setting.
              </li>
              <li>
                Does <strong>not</strong> send anything. You still have to
                click "Run tick now" to trigger the email + SMS.
              </li>
            </ul>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid={
                  confirm === "48h"
                    ? "trial-reminders-prepare-cancel"
                    : "trial-reminders-prepare-expired-cancel"
                }
                disabled={prep48hBusy || prepExpBusy}
                onClick={() => setConfirm(null)}
                className="text-sm text-[#B7D2AF] hover:text-white px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid={
                  confirm === "48h"
                    ? "trial-reminders-prepare-confirm"
                    : "trial-reminders-prepare-expired-confirm"
                }
                disabled={prep48hBusy || prepExpBusy}
                onClick={prepareSelf}
                className="inline-flex items-center gap-2 rounded-xl bg-[#4F6F52] hover:bg-[#5A7E5D] disabled:bg-[#2A3A2D] text-white text-sm font-semibold px-4 py-2"
              >
                {prep48hBusy || prepExpBusy ? (
                  <>
                    <Loader2 size={13} className="animate-spin" /> Preparing…
                  </>
                ) : (
                  <>
                    <FlaskConical size={13} /> Prepare my account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, error, sentAt, testid }) {
  // Renders the categorical delivery status: Sent / Not Sent / Not Eligible
  // / Failed. Uses colour + icon + tooltip so a blank field is never
  // ambiguous.
  if (status === "sent") {
    return (
      <span
        title={sentAt || ""}
        data-testid={testid}
        className="inline-flex items-center gap-1 text-xs text-[#8FBF87]"
      >
        <CheckCircle2 size={13} /> Sent
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        title={error ? `Error: ${error}` : "Send attempted but failed"}
        data-testid={testid}
        className="inline-flex items-center gap-1 text-xs text-[#E5A458]"
      >
        <AlertTriangle size={13} /> Failed
      </span>
    );
  }
  if (status === "not_eligible") {
    return (
      <span
        title={error ? `Reason: ${error}` : "Not eligible"}
        data-testid={testid}
        className="inline-flex items-center gap-1 text-xs text-[#8FA894]"
      >
        <MinusCircle size={13} /> Not eligible
      </span>
    );
  }
  return (
    <span
      title="Not sent yet"
      data-testid={testid}
      className="inline-flex items-center gap-1 text-xs text-[#6B7A6B]"
    >
      <Clock size={13} /> Not sent
    </span>
  );
}

function PlanBadge({ status }) {
  const styles = {
    pro: "bg-[#0F1B14] border-[#4F6F52]/60 text-[#8FBF87]",
    trial: "bg-[#1B1811] border-[#8C7A4F]/60 text-[#E5A458]",
    free: "bg-white/5 border-white/10 text-[#8FA894]",
  };
  const label = status === "pro" ? "Pro" : status === "trial" ? "Trial" : "Free";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] font-semibold ${styles[status] || styles.free}`}
      data-testid={`plan-badge-${status}`}
    >
      {label}
    </span>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 16).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}
